<template>
  <!-- Loading Overlay -->
  <div v-if="loading" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
    <div class="flex flex-col items-center p-8 bg-white/10 rounded-2xl backdrop-blur-md">
      <!-- Spinner moderno -->
      <div class="relative w-16 h-16">
        <div class="absolute inset-0 border-4 border-white/20 rounded-full"></div>
        <div class="absolute inset-0 border-4 border-t-transparent border-white rounded-full animate-spin"></div>
        <div class="absolute inset-0 border-4 border-transparent border-t-indigo-400 rounded-full animate-spin" style="animation-duration: 1.5s;"></div>
      </div>
      <div class="mt-4 text-white text-sm font-medium tracking-wide">Cargando datos...</div>
      <div class="mt-2 w-32 h-1 bg-white/20 rounded-full overflow-hidden">
        <div class="h-full bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full animate-pulse" style="width: 60%;"></div>
      </div>
    </div>
  </div>

  <!-- Contenido principal -->
  <div class="p-6 max-w-4xl mx-auto">
    <!-- Header con gradiente moderno -->
    <div class="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 mb-6 shadow-xl">
      <div class="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
      <div class="relative z-10 flex flex-wrap items-center justify-between gap-4">
        <div class="flex items-center gap-4 flex-wrap">
          <span class="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white/90 text-xs font-mono">
            &#60;DownloadData&#62;
          </span>
          <span class="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white/90 text-xs font-mono">
            /api/v1/presupuestos/xls/
          </span>
        </div>
        <div class="flex items-center gap-2 text-white/80 text-xs">
          <span class="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
          API Disponible
        </div>
      </div>
    </div>

    <!-- Card principal -->
    <div class="bg-white/80   rounded-2xl shadow-2xl border border-white/20 p-8">
      <!-- Título -->
      <div class="text-center mb-8">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 mb-4">
          <svg class="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-gray-800">
          Descargar datos en formato Excel
        </h2>
        <p class="text-sm text-gray-500 mt-1">
          Selecciona el centro de costo y el año para generar la descarga
        </p>
      </div>

      <!-- Controles -->
      <div class="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div class="flex flex-col md:flex-row items-center justify-center gap-4">
          <!-- Select Centro -->
          <div class="w-full md:w-[450px]">
            <label class="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
              📍 Centro de Costo
            </label>
            <select 
              v-model="centroSeleccionado" 
              class="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white shadow-sm text-sm text-gray-700 
                     focus:ring-2 focus:ring-indigo-400 focus:border-transparent outline-none transition-all duration-200
                     hover:border-indigo-300"
            >
              <option value="">Seleccione...</option>
              <option value="0" class="font-semibold text-indigo-600">📊 Todos los centros</option>
              <option v-for="item in listado" :key="item.id" :value="item.id">
                {{ item.nombre }}
              </option>
            </select>
          </div>

          <!-- Input Año -->
          <div class="w-full md:w-[150px]">
            <label class="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
              📅 Año
            </label>
            <input 
              type="text" 
              v-model="aaSeleccionado" 
      
              class="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white shadow-sm text-sm text-gray-700 
                     focus:ring-2 focus:ring-indigo-400 focus:border-transparent outline-none transition-all duration-200
                     hover:border-indigo-300 text-center font-mono"
            />
          </div>

          <!-- Botón Buscar -->
          <div class="w-full md:w-auto mt-2 md:mt-0">
            <label class="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 md:invisible">
              Acción
            </label>
            <button 
              @click="leepresupuestos()" 
              class="w-full md:w-auto px-8 py-3 rounded-xl text-sm font-medium transition-all duration-300 shadow-md hover:shadow-xl active:scale-95 flex items-center justify-center gap-2"
              :class="centroSeleccionado
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700' 
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'"
              :disabled="!centroSeleccionado"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Buscar
            </button>
          </div>
        </div>

        <!-- Indicadores de estado -->
        <div class="flex flex-wrap items-center justify-center gap-3 mt-4 pt-4 border-t border-gray-200">
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span class="w-1.5 h-1.5 rounded-full" :class="centroSeleccionado ? 'bg-green-500' : 'bg-gray-300'"></span>
            {{ centroSeleccionado ? 'Centro seleccionado' : 'Selecciona un centro' }}
          </div>
          <div class="w-px h-4 bg-gray-300"></div>
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span class="w-1.5 h-1.5 rounded-full" :class="aaSeleccionado ? 'bg-green-500' : 'bg-gray-300'"></span>
            {{ aaSeleccionado ? 'Año: ' + aaSeleccionado : 'Ingresa un año' }}
          </div>
          <div class="w-px h-4 bg-gray-300"></div>
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span class="w-1.5 h-1.5 rounded-full" :class="listado && listado.length > 0 ? 'bg-green-500' : 'bg-yellow-500'"></span>
            {{ listado && listado.length > 0 ? listado.length + ' centros disponibles' : 'Cargando centros...' }}
          </div>
        </div>
      </div>

      <!-- Footer informativo -->
      <div class="mt-6 text-center">
        <div class="inline-flex items-center gap-2 text-xs text-gray-400 bg-gray-50 px-4 py-2 rounded-full">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          La descarga incluirá datos de presupuestos en formato Excel (.xlsx)
        </div>
      </div>
    </div>
  </div>
</template>
<script>

import api from "@/api/axios";

 
export default {
  name: 'MaestroCentros',
  props: {
    ejemploProp: {
      type: String,
      default: ''
    },
    aa:{
      type:String
    }

  },
  data() {
    return {
      valor: '',
      listado: {},
      categorias: {},
      tipos: {},
      cuentaExpandida: [], // almacena IDs de cuentas abiertas
      cuentaExpandidaBorrador: [], // almacena IDs de cuentas abiertas
      centroSeleccionado: '',
      aaSeleccionado: this.aa,
      loading: false,
      nombreCentro: 'Centro sin seleccionar',
      componenteVisible: false,
      componenteVisibleBorrador: false,
      buscarCentro: false,
      editados: []
    }
  },
  computed: {
    centroSeleccionadoObj() {
      return this.listado.find(item => item.id === this.centroSeleccionado)
    },
    centroNombre() {
      return this.centroSeleccionadoObj?.nombre || ''
    }
  },
  methods: {

    set_total(obj) {
      console.log('set total', obj)
      if (obj) {
        this.editados[obj.cuenta_id] = obj.total
      }
    },

    toggleCuenta(id) {
      const i = this.cuentaExpandida.indexOf(id);
      if (i === -1) {
        console.log('añadir ', i)
        this.cuentaExpandida.push(id);

      } else {

        this.cuentaExpandida.splice(i, 1);

      }
    },


    toggleCuentaBorrador(id) {

      if (!this.tienePermiso(id)) {
        return false;
      }

      const i = this.cuentaExpandidaBorrador.indexOf(id);
      if (i === -1) {
        console.log('hacer visible')
        this.cuentaExpandidaBorrador.push(id);
        this.componenteVisibleBorrador = true;

      } else {
        //console.log(i)
        this.cuentaExpandidaBorrador.splice(i, 1);

      }



    },
    tienePermiso(id) {
      if (this.$store.state.auth.userLogin.is_admin){
        return true;
      }
      if ( Number(id) === Number(process.env.VUE_APP_ID_CTA_REMUNERACIONES)){
         alert('no tiene permiso para modificar esta cuenta.');
        return false;
      }
        
      return true;
      

    },
    async centros() {


      try {
        /*
        $store.state.centros.centros
        {
        id: 1
        codigo: PRO035000000000
        nombre: Centro Rescate Huemul
        tiene_presupuesto:true
        }
        */
        this.listado_original = this.$store.state.centros.centros




        this.listado = Object.values(this.listado_original).sort((a, b) =>
          a.nombre.localeCompare(b.nombre)
        );


        console.log("Inicio de sesión correcto ✅");
      } catch (e) {
        console.error("Credenciales inválidas ❌");
      }

    },

 
    async leepresupuestos() {

      if (!this.centroSeleccionado) {
        return false;
      }      
      if (!this.aaSeleccionado) {
        return false;
      }
      this.editados = []
      this.buscarCentro = true;
      this.nombreCentro = this.centroNombre
      this.cuentaExpandidaBorrador = []

      this.loading = true

 
   

      try {
        const data = {'centro_id':this.centroSeleccionado, 'aa':this.aaSeleccionado}
        

          const response = await api.post(
            '/api/v1/presupuestos/xls/',data ,
            {
              responseType: 'blob'
            }
          )

          const blob = new Blob(
            [response.data],
            {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
          )

          const url = window.URL.createObjectURL(blob)

          const link = document.createElement('a')
          link.href = url
          link.download = `download_budget.xlsx`

          document.body.appendChild(link)
          link.click()

          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)

          this.loading=false 

        } catch (error) {
          console.error(error)
        }
      


    },
    revisaExpansion(cuenta_id) {

      if (this.cuentaExpandida.includes(cuenta_id)) {
        return true;
      }
      return false;
    },
    revisaExpansionBorrador(cuenta_id) {

      if (this.cuentaExpandidaBorrador.includes(cuenta_id)) {
        return true;
      }
      return false;
    },
  },
  mounted() {
   
    this.centros()
  },
  components: {
    //  IngresosMensuales, 
  }

}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>